import CT from '@marketplace-ai/tags';
import classNames from 'classnames';
import { getContractParties, getViewerSignSide } from '../contractParties';
import { ContractDetail, ContractSignatureSide } from '../types';

interface Props {
  contract: ContractDetail;
  currentEmployerId?: number;
  isProviderView?: boolean;
}

interface PartyCardProps {
  name: string;
  role: string;
  label: string;
  isCurrentUser: boolean;
  signature?: { signerName: string; signedAt: string } | null;
}

// A signature is a moment, so it reads in the viewer's own time zone.
const formatSignedOn = (signedAt: string) =>
  new Date(signedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const PartyCard = ({ name, role, label, isCurrentUser, signature }: PartyCardProps) => (
  <div
    className={classNames(
      'flex-1 p-3 rounded-lg border',
      isCurrentUser ? 'border-primary-50 bg-primary-t-900' : 'border-grey-800 bg-grey-900'
    )}
  >
    <p className="text-xs uppercase tracking-wide text-grey-500 mb-0.5">{role}</p>
    <p className="text-sm font-bold text-grey-50">{name}</p>
    <p className="text-xs text-grey-500">{label}</p>
    {/* [MCV-1829] The typed-name e-signature of this side, once it has signed. */}
    {signature && (
      <p
        className="text-xs text-grey-50 mt-1"
        data-cy={CT.employer.contracts.contractDetail.partySignature}
      >
        {`Signed by ${signature.signerName} \u00B7 ${formatSignedOn(signature.signedAt)}`}
      </p>
    )}
  </div>
);

export const ContractPartyHeader = ({ contract, currentEmployerId, isProviderView }: Props) => {
  const hasCurrentEmployer = currentEmployerId !== undefined;
  const isHiring = hasCurrentEmployer && contract.listingEmployerId === currentEmployerId;
  const parties = getContractParties(contract);
  const signatureFor = (side: ContractSignatureSide) =>
    (contract.signatures ?? []).find((signature) => signature.side === side) ?? null;

  // [MCV-1727] A provider is never the current employer, so their own card comes
  // from the side they sign: the listing card for a SERVICE contract (they listed it), the
  // contracted card for a JOB contract (they were hired). They are not a party to an MSAAS
  // contract, so neither card is theirs. Employer highlighting is unchanged.
  const providerSide = isProviderView ? getViewerSignSide(contract, { isProviderView }) : null;
  const isListingCurrent = isProviderView
    ? providerSide === 'listing'
    : hasCurrentEmployer && isHiring;
  const isContractedCurrent = isProviderView
    ? providerSide === 'contracted'
    : hasCurrentEmployer && !isHiring;

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-3">
      <PartyCard
        name={parties.listing.name}
        role={parties.listing.role}
        label={parties.listing.label}
        isCurrentUser={isListingCurrent}
        signature={signatureFor('LISTING')}
      />
      <span className="text-grey-500 text-sm max-sm:hidden">&#x27F7;</span>
      <PartyCard
        name={parties.contracted.name}
        role={parties.contracted.role}
        label={parties.contracted.label}
        isCurrentUser={isContractedCurrent}
        signature={signatureFor('CONTRACTED')}
      />
    </div>
  );
};
